import {ReactElement, useCallback, useState} from 'react'
import {useHistory} from 'react-router-dom'
import get from 'lodash/get'
import {Trans, WithNamespaces, withNamespaces} from 'react-i18next'

import {
  Account,
  ClientType,
  Maybe,
  WithdrawablePaymentMethod,
  ClientBankDetails,
} from '../../../../graphql/types'
import {getFormattedAmount} from '../../../../common/utils'
import {ChooseWithdrawalMethod} from './ChooseWithdrawalMethod'
import {RefundInformation} from './RefundInformation'
import {WithdrawalSummaryFooter} from './WithdrawalSummaryFooter'
import {RefundSection} from './RefundSection'
import {ConfirmSubmitModal} from './ConfirmSubmitModal'
import {SuccessSubmitModal} from './SuccessSubmitModal'
import {FailedSubmitModal} from './FailedSubmitModal'
import messages from '../../../../assets/messages'
import {WithdrawalMethodValues, WITHDRAWAL_METHODS_LIST} from './constants'
import {DepositMethodVerificationSubmittedModal} from './DepositMethodVerificationSubmittedModal'
import {DepositMethodVerificationInReviewModal} from './DepositMethodVerificationInReviewModal'
import {DepositMethodVerificationRejectedModal} from './DepositMethodVerificationRejectedModal'
import {ConfirmChangeWithdrawalMethodModal} from './ConfirmChangeWithdrawalMethodModal'

import {
  buildRefundWithdrawSchema,
  buildWithdrawalsParams,
  getNoReadRejectedId,
  getIsIssueInReview,
  getWithdrawalMethodMinAmount,
} from './utils'
import NotificationBar from '../../../Common/NotificationBar'
import {useCreateWithdrawalsRequest, useFetchClientDepositMethodIssueVerifications} from './hooks'
import {useStyles} from './styles'

type WithdrawalSummaryProps = WithNamespaces & {
  account: Account
  clientId: number
  bankDetails: ClientBankDetails | null
  clientType: Maybe<ClientType>
  clientCountry: Maybe<string>
  passportNumber: Maybe<string>
  amountToWithdraw: number
  withdrawablePaymentMethods: WithdrawablePaymentMethod[]
  redirectBack: () => void
}

function WithdrawalSummaryBase(props: WithdrawalSummaryProps): ReactElement | null {
  const classes = useStyles()
  const history = useHistory()
  const {t} = props
  const {
    account,
    amountToWithdraw,
    redirectBack,
    withdrawablePaymentMethods,
    clientId,
    bankDetails,
    clientType,
    clientCountry,
    passportNumber,
  } = props
  const {createWithdrawalRequests, loading} = useCreateWithdrawalsRequest()
  const {depositMethodIssueVerifications} = useFetchClientDepositMethodIssueVerifications(clientId)

  const [activeWithdrawalMethod, setActiveWithdrawalMethod] =
    useState<WithdrawalMethodValues | null>(null)
  const [isAgreementChecked, setIsAgreementChecked] = useState<boolean>(false)

  const [isConfirmSubmitModalOpen, setIsConfirmSubmitModalOpen] = useState<boolean>(false)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false)
  const [
    isDepositMethodVerificationSubmittedModalOpen,
    setIsDepositMethodVerificationSubmittedModalOpen,
  ] = useState<boolean>(false)
  const [
    withdrawalPaymentMethodChangeToConfirmValue,
    setWithdrawalPaymentMethodChangeToConfirmValue,
  ] = useState<WithdrawalMethodValues | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const [bankTransferPaymentFields, setBankTransferPaymentFields] = useState(null)
  const [cryptoPaymentFields, setCryptoPaymentFields] = useState(null)
  const [cryptoPayoutPaymentFields, setCryptoPayoutPaymentFields] = useState(null)
  const [dfnsCryptoPayoutPaymentFields, setDfnsCryptoPayoutPaymentFields] = useState(null)
  const [payRetailersPaymentFields, setPayRetailersPaymentFields] = useState(null)
  const [pixPayoutPaymentFields, setPixPayoutPaymentFields] = useState(null)

  const handleBankTransferValuesSet = (fieldValues) => setBankTransferPaymentFields(fieldValues)
  const handleCryptoValuesSet = (fieldValues) => setCryptoPaymentFields(fieldValues)
  const handleCryptoPayoutValuesSet = (fieldValues) => setCryptoPayoutPaymentFields(fieldValues)
  const handleDfnsCryptoPayoutValuesSet = (fieldValues) =>
    setDfnsCryptoPayoutPaymentFields(fieldValues)
  const handlePayRetailersValuesSet = (fieldValues) => setPayRetailersPaymentFields(fieldValues)
  const handlePixPayoutPaymentFields = (fieldValues) => setPixPayoutPaymentFields(fieldValues)

  // We are unable to properly scale func below, I did some improvements,
  // but it requires refactoring
  const handleMethodChange = useCallback(
    (withdrawalMethod: WithdrawalMethodValues) => {
      const paymentFields = [
        bankTransferPaymentFields,
        cryptoPaymentFields,
        cryptoPayoutPaymentFields,
        dfnsCryptoPayoutPaymentFields,
        payRetailersPaymentFields,
        pixPayoutPaymentFields,
      ]
      if (
        withdrawalMethod === WithdrawalMethodValues.bankTransfer &&
        paymentFields.filter((pf) => pf !== bankTransferPaymentFields).some(Boolean)
      ) {
        return setWithdrawalPaymentMethodChangeToConfirmValue(withdrawalMethod)
      }
      if (
        withdrawalMethod === WithdrawalMethodValues.cryptocurrency &&
        paymentFields.filter((pf) => pf !== cryptoPaymentFields).some(Boolean)
      ) {
        return setWithdrawalPaymentMethodChangeToConfirmValue(withdrawalMethod)
      }
      if (
        withdrawalMethod === WithdrawalMethodValues.enovipayCryptoPayout &&
        paymentFields.filter((pf) => pf !== cryptoPayoutPaymentFields).some(Boolean)
      ) {
        return setWithdrawalPaymentMethodChangeToConfirmValue(withdrawalMethod)
      }
      if (
        withdrawalMethod === WithdrawalMethodValues.dfnsCryptoPayout &&
        paymentFields.filter((pf) => pf !== dfnsCryptoPayoutPaymentFields).some(Boolean)
      ) {
        return setWithdrawalPaymentMethodChangeToConfirmValue(withdrawalMethod)
      }
      if (
        withdrawalMethod === WithdrawalMethodValues.payRetailers &&
        paymentFields.filter((pf) => pf !== payRetailersPaymentFields).some(Boolean)
      ) {
        return setWithdrawalPaymentMethodChangeToConfirmValue(withdrawalMethod)
      }
      if (
        withdrawalMethod === WithdrawalMethodValues.enovipayPixPayout &&
        paymentFields.filter((pf) => pf !== pixPayoutPaymentFields).some(Boolean)
      ) {
        return setWithdrawalPaymentMethodChangeToConfirmValue(withdrawalMethod)
      }

      setActiveWithdrawalMethod(withdrawalMethod)
    },
    [
      bankTransferPaymentFields,
      cryptoPaymentFields,
      cryptoPayoutPaymentFields,
      dfnsCryptoPayoutPaymentFields,
      payRetailersPaymentFields,
      pixPayoutPaymentFields,
    ],
  )
  const handleAgreementChange = () => setIsAgreementChecked((prevValue) => !prevValue)

  const {totalRemainingAmount, refundWithdrawSchema, totalAmountToRefund} =
    buildRefundWithdrawSchema({
      withdrawablePaymentMethods,
      amountToWithdraw,
      depositMethodIssueVerifications,
    })

  // `title` is a <Trans {...messages.xxx} /> element, so its props contain the
  // message descriptor (i18nKey/defaults) directly.
  const activeWithdrawalMethodTitleProps = get(
    WITHDRAWAL_METHODS_LIST.find(({value}) => value === activeWithdrawalMethod),
    'title.props',
  )
  const activeWithdrawalMethodName = activeWithdrawalMethodTitleProps?.i18nKey
    ? t(activeWithdrawalMethodTitleProps.i18nKey, activeWithdrawalMethodTitleProps.defaults)
    : ''

  const isIssueInReviewState = getIsIssueInReview({
    refundWithdrawSchema,
    depositMethodIssueVerifications,
  })
  const issueRejectedNoReadId = isIssueInReviewState
    ? null
    : getNoReadRejectedId({refundWithdrawSchema, depositMethodIssueVerifications})

  const isCardRefundSchemaPresented = Boolean(refundWithdrawSchema.length)
  const isChooseWithdrawMethodShown = totalRemainingAmount > 0

  const handleConfirmSubmitModalOpen = () => setIsConfirmSubmitModalOpen(true)
  const handleConfirmSubmitModalClose = () => setIsConfirmSubmitModalOpen(false)
  const handleFailedModalClose = () => setSubmitError(null)
  const handleDepositMethodVerificationSubmittedModalOpen = () =>
    setIsDepositMethodVerificationSubmittedModalOpen(true)
  const handleCloseConfirmChangeModal = () => setWithdrawalPaymentMethodChangeToConfirmValue(null)

  const handleConfirmChangePaymentMethod = (
    withdrawalMethodValueToConfirm: WithdrawalMethodValues,
  ) => {
    const stateSetters = [
      setBankTransferPaymentFields,
      setCryptoPaymentFields,
      setCryptoPayoutPaymentFields,
      setDfnsCryptoPayoutPaymentFields,
      setPayRetailersPaymentFields,
      setPixPayoutPaymentFields,
    ]
    if (withdrawalMethodValueToConfirm === WithdrawalMethodValues.bankTransfer) {
      stateSetters
        .filter((ss) => ss !== setBankTransferPaymentFields)
        .forEach((setState) => setState(null))
    }
    if (withdrawalMethodValueToConfirm === WithdrawalMethodValues.cryptocurrency) {
      stateSetters
        .filter((ss) => ss !== setCryptoPaymentFields)
        .forEach((setState) => setState(null))
    }

    if (withdrawalMethodValueToConfirm === WithdrawalMethodValues.enovipayCryptoPayout) {
      stateSetters
        .filter((ss) => ss !== setCryptoPayoutPaymentFields)
        .forEach((setState) => setState(null))
    }

    if (withdrawalMethodValueToConfirm === WithdrawalMethodValues.dfnsCryptoPayout) {
      stateSetters
        .filter((ss) => ss !== setDfnsCryptoPayoutPaymentFields)
        .forEach((setState) => setState(null))
    }

    if (withdrawalMethodValueToConfirm === WithdrawalMethodValues.enovipayPixPayout) {
      stateSetters
        .filter((ss) => ss !== setPixPayoutPaymentFields)
        .forEach((setState) => setState(null))
    }

    if (withdrawalMethodValueToConfirm === WithdrawalMethodValues.payRetailers) {
      stateSetters
        .filter((ss) => ss !== setPayRetailersPaymentFields)
        .forEach((setState) => setState(null))
    }

    setActiveWithdrawalMethod(withdrawalMethodValueToConfirm)
    setWithdrawalPaymentMethodChangeToConfirmValue(null)
  }

  const handleSuccessModalClose = () => {
    setIsSuccessModalOpen(false)
    history.push('/')
  }

  const isBankTransferReady =
    activeWithdrawalMethod === WithdrawalMethodValues.bankTransfer && bankTransferPaymentFields
  const isCryptoTransferReady =
    activeWithdrawalMethod === WithdrawalMethodValues.cryptocurrency && cryptoPaymentFields
  const isCryptoPayoutReady =
    activeWithdrawalMethod === WithdrawalMethodValues.enovipayCryptoPayout &&
    cryptoPayoutPaymentFields
  const isDfnsCryptoPayoutReady =
    activeWithdrawalMethod === WithdrawalMethodValues.dfnsCryptoPayout &&
    dfnsCryptoPayoutPaymentFields
  const isPixPayoutReady =
    activeWithdrawalMethod === WithdrawalMethodValues.enovipayPixPayout && pixPayoutPaymentFields
  const isPayRetailersReady =
    activeWithdrawalMethod === WithdrawalMethodValues.payRetailers && payRetailersPaymentFields
  const activeWithdrawalMethodMinAmount = getWithdrawalMethodMinAmount(
    activeWithdrawalMethod,
    clientCountry,
  )
  // totalRemainingAmount is the amount sent to the selected payout vendor in
  // buildWithdrawalsParams: refunds are submitted as separate withdrawal
  // requests within the same batch, so the min amount applies to it only.
  const isBelowWithdrawalMinAmount =
    activeWithdrawalMethodMinAmount && totalRemainingAmount < activeWithdrawalMethodMinAmount
  const totalRemainingAmountReady =
    totalRemainingAmount > 0
      ? isBankTransferReady ||
        isCryptoTransferReady ||
        isCryptoPayoutReady ||
        isDfnsCryptoPayoutReady ||
        isPayRetailersReady ||
        isPixPayoutReady
      : true
  const isSubmitActive =
    totalRemainingAmountReady &&
    isAgreementChecked &&
    !isIssueInReviewState &&
    !issueRejectedNoReadId &&
    !isBelowWithdrawalMinAmount

  const handleRequestSend = async () => {
    if (!isSubmitActive) return

    try {
      const params = buildWithdrawalsParams({
        account,
        refundWithdrawSchema,
        activeWithdrawalMethod,
        bankTransferPaymentFields,
        cryptoPaymentFields,
        cryptoPayoutPaymentFields,
        dfnsCryptoPayoutPaymentFields,
        payRetailersPaymentFields,
        totalRemainingAmount,
        pixPayoutPaymentFields,
      })
      await createWithdrawalRequests(params)

      setIsSuccessModalOpen(true)
    } catch (error: unknown) {
      if (error instanceof Error && error?.message) {
        const errorMessage = get(error, 'graphQLErrors[0].message') || error.message
        setSubmitError(errorMessage)
      } else {
        setSubmitError('Something went wrong')
      }
    } finally {
      handleConfirmSubmitModalClose()
    }
  }

  const onlyCardRefundMethodFlow = isCardRefundSchemaPresented && !isChooseWithdrawMethodShown
  const onlySelectPaymentMethodFlow = !isCardRefundSchemaPresented && isChooseWithdrawMethodShown
  const showRefundInformation = onlyCardRefundMethodFlow || onlySelectPaymentMethodFlow

  if (!account.id) return null

  return (
    <>
      <DepositMethodVerificationSubmittedModal
        isOpen={isDepositMethodVerificationSubmittedModalOpen}
      />
      {!isDepositMethodVerificationSubmittedModalOpen && isIssueInReviewState ? (
        <DepositMethodVerificationInReviewModal />
      ) : null}
      {issueRejectedNoReadId ? (
        <DepositMethodVerificationRejectedModal issueRejectedNoReadId={issueRejectedNoReadId} />
      ) : null}
      <SuccessSubmitModal isOpen={isSuccessModalOpen} onClose={handleSuccessModalClose} />
      <ConfirmChangeWithdrawalMethodModal
        withdrawalMethod={withdrawalPaymentMethodChangeToConfirmValue}
        onClose={handleCloseConfirmChangeModal}
        onConfirm={handleConfirmChangePaymentMethod}
      />
      <FailedSubmitModal
        amountToWithdraw={getFormattedAmount({
          amount: amountToWithdraw,
          currency: account.currency,
        })}
        errorMessage={submitError}
        onClose={handleFailedModalClose}
      />
      <ConfirmSubmitModal
        account={account}
        isSubmitting={loading}
        isOpen={isConfirmSubmitModalOpen}
        activeWithdrawalMethod={activeWithdrawalMethod}
        totalAmountToRefund={totalAmountToRefund}
        totalRemainingAmount={totalRemainingAmount}
        bankTransferPaymentFields={bankTransferPaymentFields}
        cryptoPaymentFields={cryptoPaymentFields}
        payRetailersPaymentFields={payRetailersPaymentFields}
        cryptoPayoutPaymentFields={cryptoPayoutPaymentFields}
        dfnsCryptoPayoutPaymentFields={dfnsCryptoPayoutPaymentFields}
        pixPayoutPaymentFields={pixPayoutPaymentFields}
        onClose={handleConfirmSubmitModalClose}
        onRequestSend={handleRequestSend}
      />
      <div className={classes.container}>
        <div className={classes.content}>
          {isCardRefundSchemaPresented ? (
            <RefundSection
              accountId={account.id}
              clientId={clientId}
              totalAmountToRefund={totalAmountToRefund}
              amountsToRefund={refundWithdrawSchema}
              currency={account.currency}
              refundWithdrawSchema={refundWithdrawSchema}
              withRefundInfo={isChooseWithdrawMethodShown}
              onlySelectPaymentMethodFlow={onlySelectPaymentMethodFlow}
              onDepositMethodVerificationSubmittedModalOpen={
                handleDepositMethodVerificationSubmittedModalOpen
              }
            />
          ) : null}
          {isChooseWithdrawMethodShown ? (
            <ChooseWithdrawalMethod
              availableWithdrawalOptions={account.availableWithdrawalOptions || []}
              isCardRefundSchemaPresented={isCardRefundSchemaPresented}
              activeWithdrawalMethod={activeWithdrawalMethod}
              remainingWithdrawalAmount={getFormattedAmount({
                amount: totalRemainingAmount,
                currency: account.currency,
              })}
              bankTransferPaymentFields={bankTransferPaymentFields}
              clientBankDetails={bankDetails}
              onBankTransferSet={handleBankTransferValuesSet}
              cryptoPaymentFields={cryptoPaymentFields}
              onCryptoSet={handleCryptoValuesSet}
              cryptoPayoutPaymentFields={cryptoPayoutPaymentFields}
              onCryptoPayoutSet={handleCryptoPayoutValuesSet}
              dfnsCryptoPayoutPaymentFields={dfnsCryptoPayoutPaymentFields}
              onDfnsCryptoPayoutSet={handleDfnsCryptoPayoutValuesSet}
              payRetailersPaymentFields={payRetailersPaymentFields}
              onPayRetailersSet={handlePayRetailersValuesSet}
              pixPayoutPaymentFields={pixPayoutPaymentFields}
              onPixPayoutSet={handlePixPayoutPaymentFields}
              clientType={clientType}
              passportNumber={passportNumber}
              onChange={handleMethodChange}
            />
          ) : null}
          {showRefundInformation ? (
            <RefundInformation
              clientId={clientId}
              accountId={account.id}
              refundWithdrawSchema={refundWithdrawSchema}
              onlySelectPaymentMethodFlow={onlySelectPaymentMethodFlow}
              additionalItems={
                onlyCardRefundMethodFlow ? (
                  <li>
                    <Trans
                      {...messages.cardPaymentProcessingTime}
                      components={[
                        <span className={classes.cardPaymentProcessingTime}>
                          cardPaymentProcessingTime
                        </span>,
                      ]}
                    />
                  </li>
                ) : null
              }
              onDepositMethodVerificationSubmittedModalOpen={
                handleDepositMethodVerificationSubmittedModalOpen
              }
            />
          ) : null}
        </div>
        <div className={classes.divider} />
        {isBelowWithdrawalMinAmount ? (
          <NotificationBar status="error" className={classes.withdrawalWarningContainer}>
            <Trans
              {...messages.minimumWithdrawalAmount}
              values={{
                vendor: activeWithdrawalMethodName,
                amount: getFormattedAmount({
                  amount: activeWithdrawalMethodMinAmount,
                  currency: account.currency,
                }),
              }}
            />
          </NotificationBar>
        ) : null}
        <WithdrawalSummaryFooter
          account={account}
          isSubmitDisabled={!isSubmitActive}
          amountToWithdraw={amountToWithdraw}
          isAgreementTermsChecked={isAgreementChecked}
          onIsAgreementTermsChange={handleAgreementChange}
          redirectBack={redirectBack}
          onSubmit={handleConfirmSubmitModalOpen}
        />
      </div>
    </>
  )
}

const WithdrawalSummary = withNamespaces()(WithdrawalSummaryBase)

export {WithdrawalSummary}
